/**
 * Currency registry — Phase-1 multi-currency.
 *
 * Balances live in 4 currencies: BDT (৳), INR (₹), PKR (₨), USDT ($T).
 * The backend stores each currency natively; there is NO display-currency
 * conversion anywhere on the frontend. This module used to expose an FX
 * rate table (`rate: 110` etc.) — it's been gutted. Only symbols, labels,
 * and pure formatting helpers remain.
 */
export const CURRENCIES = [
  { code: "BDT",  label: "Bangladeshi Taka", symbol: "৳",  short: "BDT",  decimals: 2, prefix: true,  group: "fiat",   description: "Bangladeshi Taka" },
  { code: "INR",  label: "Indian Rupee",     symbol: "₹",  short: "INR",  decimals: 2, prefix: true,  group: "fiat",   description: "Indian Rupee" },
  { code: "PKR",  label: "Pakistani Rupee",  symbol: "₨",  short: "PKR",  decimals: 2, prefix: true,  group: "fiat",   description: "Pakistani Rupee" },
  { code: "USDT", label: "Tether · TRC20",   symbol: "$T", short: "USDT", decimals: 2, prefix: false, group: "crypto", description: "Tether TRC20 · crypto" },
];

const BY_CODE = Object.fromEntries(CURRENCIES.map((c) => [c.code, c]));

export const SUPPORTED_CODES = CURRENCIES.map((c) => c.code);
export const DEFAULT_CURRENCY = "USDT";

/** Per-currency min/max BET amounts (matches backend/currency.py). */
export const MIN_BET_BY_CURRENCY = { BDT: 1, INR: 1, PKR: 1, USDT: 0.1 };
export const MAX_BET_BY_CURRENCY = { BDT: 10000, INR: 10000, PKR: 10000, USDT: 100 };

/** Suggested bet-chip amounts per currency for the game screen. */
export const BET_CHIPS_BY_CURRENCY = {
  BDT: [10, 50, 100, 500, 1000],
  INR: [10, 50, 100, 500, 1000],
  PKR: [10, 50, 100, 500, 1000],
  USDT: [0.1, 0.5, 1, 5, 10],
};

/** Suggested DEPOSIT-chip amounts per currency for the wallet screen. */
export const DEPOSIT_CHIPS_BY_CURRENCY = {
  BDT: [500, 1000, 2000, 5000, 10000],
  USDT: [10, 50, 100, 500, 1000],
};

/** Deposit rails currently ENABLED per currency. */
export const DEPOSIT_ENABLED = { BDT: true, USDT: true, INR: false, PKR: false };
/** Withdrawal rails currently ENABLED per currency. */
export const WITHDRAW_ENABLED = { USDT: true, BDT: false, INR: false, PKR: false };

export function getCurrency(code) {
  return BY_CODE[code] || BY_CODE[DEFAULT_CURRENCY];
}

/**
 * Format a native-currency amount (NOT a USD value). USDT renders `$T 12.34`,
 * fiat renders `৳ 1,234.00`. Zero balances get a compact placeholder when
 * `compactZero` is passed.
 */
export function formatAmount(amount, code = DEFAULT_CURRENCY, { compactZero = false } = {}) {
  const cur = getCurrency(code);
  const value = Number(amount || 0);
  if (compactZero && value <= 0) return `${cur.symbol}—`;
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: cur.decimals,
    maximumFractionDigits: cur.decimals,
  }).format(value);
  return cur.prefix ? `${cur.symbol}${formatted}` : `${cur.symbol} ${formatted}`;
}

/** Pull one bucket from a user's `balances_real` / `balances_demo` /
 *  `balances_bonus` map. `mode` is one of "real" | "demo" | "bonus". */
export function getBalance(user, mode, currency) {
  if (!user) return 0;
  const map =
    mode === "real" ? user.balances_real :
    mode === "bonus" ? user.balances_bonus :
    user.balances_demo;
  if (!map) return 0;
  return Number(map[currency] || 0);
}

/** Integer-only display helper (legacy — some components pass raw demo
 *  credits without a currency code). Kept so existing call sites don't
 *  break; NEW code should use `formatAmount` with a currency. */
export function formatDemoCredits(n) {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number(n || 0));
}
